import React from "react";
import { Add, ArrowDownward, ArrowUpward, DeleteOutline, DragIndicator } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  createWorkflowConditionPath,
  createWorkflowCriterion,
  WorkflowCanvas,
  WorkflowConditionCriterion,
  WorkflowConditionLogic,
  WorkflowConditionOperator,
  WorkflowConditionPath,
  WORKFLOW_CONDITION_OPERATOR_LABELS,
  WORKFLOW_END_ROUTE,
  WORKFLOW_END_ROUTE_LABEL,
  WORKFLOW_NUMBER_ONLY_OPERATORS as NUMBER_ONLY_OPERATORS,
  workflowNumberVariableTest,
} from "./workflowBuilderData";
import { useWorkflowReadOnly } from "./WorkflowReadOnlyContext";

interface WorkflowConditionalPanelProps {
  paths: WorkflowConditionPath[];
  canvases: WorkflowCanvas[];
  currentCanvasId: string;
  onChange: (paths: WorkflowConditionPath[]) => void;
}

const OPERATORS = Object.entries(WORKFLOW_CONDITION_OPERATOR_LABELS) as Array<
  [WorkflowConditionOperator, string]
>;

const PATH_DRAG_TYPE = "application/x-workflow-path";


const WorkflowConditionalPanel: React.FC<WorkflowConditionalPanelProps> = ({
  paths,
  canvases,
  currentCanvasId,
  onChange,
}) => {
  const viewOnly = useWorkflowReadOnly();
  const routeOptions = canvases.filter((canvas) => canvas.id !== currentCanvasId);
  const isNumberVariable = workflowNumberVariableTest(canvases);

  const updatePath = (pathId: string, patch: Partial<WorkflowConditionPath>) => {
    onChange(paths.map((path) => (path.id === pathId ? { ...path, ...patch } : path)));
  };

  const updateCriterion = (
    path: WorkflowConditionPath,
    criterionId: string,
    patch: Partial<WorkflowConditionCriterion>,
  ) => {
    updatePath(path.id, {
      criteria: path.criteria.map((criterion) =>
        criterion.id === criterionId ? { ...criterion, ...patch } : criterion,
      ),
    });
  };

  // Re-pointing a > / < criterion at a non-number field drops the operator back to "is".
  const handleVariableChange = (
    path: WorkflowConditionPath,
    criterion: WorkflowConditionCriterion,
    variable: string,
  ) => {
    const keepsOperator = !NUMBER_ONLY_OPERATORS.has(criterion.operator) || isNumberVariable(variable);
    updateCriterion(path, criterion.id, {
      variable,
      ...(keepsOperator ? {} : { operator: "is" as const }),
    });
  };

  // Path order IS evaluation order (first match wins), so reordering changes the flow; it maps to
  // the path transitions' SortVal on save.
  const movePath = (from: number, to: number) => {
    if (from === to || to < 0 || to >= paths.length) return;
    const reordered = [...paths];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    onChange(reordered);
  };

  const handlePathDragStart = (event: React.DragEvent<HTMLElement>, pathId: string) => {
    // Sidenav's react-dnd HTML5Backend has a window dragstart listener that cancels any drag it
    // doesn't own; stopping propagation keeps this one alive, as the field and option grips do.
    event.stopPropagation();
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(PATH_DRAG_TYPE, pathId);
  };

  // Only a path drag is accepted here, not a field or option drag from elsewhere in the dialog.
  const handlePathDragOver = (event: React.DragEvent<HTMLElement>) => {
    if (!event.dataTransfer.types.includes(PATH_DRAG_TYPE)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handlePathDrop = (event: React.DragEvent<HTMLElement>, targetId: string) => {
    const sourceId = event.dataTransfer.getData(PATH_DRAG_TYPE);
    if (!sourceId) return;
    event.preventDefault();
    movePath(
      paths.findIndex((path) => path.id === sourceId),
      paths.findIndex((path) => path.id === targetId),
    );
  };

  const removeCriterion = (path: WorkflowConditionPath, criterionId: string) => {
    updatePath(path.id, {
      criteria: path.criteria.filter((criterion) => criterion.id !== criterionId),
    });
  };

  return (
    <Box className="workflow-canvas-dialog__config-panel">
      <Alert severity="info">
        Paths are checked top-to-bottom. <strong>First match wins</strong>, so put the most
        specific path first. Greater/less-than comparisons should only use number fields.
      </Alert>

      {paths.map((path, pathIndex) => (
        <Box
          className="workflow-canvas-dialog__config-card"
          key={path.id}
          onDragOver={viewOnly ? undefined : handlePathDragOver}
          onDrop={viewOnly ? undefined : (event) => handlePathDrop(event, path.id)}
        >
          <Box className="workflow-canvas-dialog__config-card-heading">
            {!viewOnly && (
              <Tooltip title="Drag to reorder" placement="top">
                <span
                  className="workflow-canvas-dialog__grip"
                  draggable
                  onDragStart={(event) => handlePathDragStart(event, path.id)}
                  aria-label={`Reorder ${path.name || `path ${pathIndex + 1}`} by dragging`}
                >
                  <DragIndicator />
                </span>
              </Tooltip>
            )}
            <TextField
              disabled={viewOnly}
              label="Path name"
              size="small"
              fullWidth
              value={path.name}
              onChange={(event) => updatePath(path.id, { name: event.target.value })}
            />
            <Typography>{pathIndex === 0 ? "1st checked" : `#${pathIndex + 1} checked`}</Typography>
            {!viewOnly && (
              <>
                {/* The keyboard alternative to dragging; a disabled button needs a span for its Tooltip. */}
                <Tooltip title="Move up (checked earlier)" placement="top">
                  <span className="d-inline-flex">
                    <IconButton
                      aria-label={`Move path ${path.name || pathIndex + 1} up`}
                      disabled={pathIndex === 0}
                      onClick={() => movePath(pathIndex, pathIndex - 1)}
                    >
                      <ArrowUpward />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move down (checked later)" placement="top">
                  <span className="d-inline-flex">
                    <IconButton
                      aria-label={`Move path ${path.name || pathIndex + 1} down`}
                      disabled={pathIndex === paths.length - 1}
                      onClick={() => movePath(pathIndex, pathIndex + 1)}
                    >
                      <ArrowDownward />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            {!viewOnly && (
              <Tooltip title="Remove path">
                <IconButton
                  aria-label={`Remove path ${path.name || pathIndex + 1}`}
                  onClick={() => onChange(paths.filter((item) => item.id !== path.id))}
                >
                  <DeleteOutline />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <FormControl disabled={viewOnly} size="small" className="workflow-canvas-dialog__logic">
            <InputLabel id={`${path.id}-logic-label`}>Match when</InputLabel>
            <Select
              labelId={`${path.id}-logic-label`}
              label="Match when"
              value={path.logic}
              onChange={(event: SelectChangeEvent) =>
                updatePath(path.id, { logic: event.target.value as WorkflowConditionLogic })
              }
            >
              <MenuItem value="AND">AND</MenuItem>
              <MenuItem value="OR">OR</MenuItem>
            </Select>
          </FormControl>

          {path.criteria.map((criterion) => {
            const canRemoveCriterion = path.criteria.length > 1;

            return (
            <Box
              className={`workflow-canvas-dialog__criterion${
                canRemoveCriterion ? "" : " workflow-canvas-dialog__criterion--no-delete"
              }`}
              key={criterion.id}
            >
              <TextField
                disabled={viewOnly}
                label="Field or variable"
                size="small"
                value={criterion.variable}
                onChange={(event) => handleVariableChange(path, criterion, event.target.value)}
              />
              <FormControl disabled={viewOnly} size="small">
                <InputLabel id={`${criterion.id}-operator-label`}>Operator</InputLabel>
                <Select
                  labelId={`${criterion.id}-operator-label`}
                  label="Operator"
                  value={criterion.operator}
                  onChange={(event: SelectChangeEvent) =>
                    updateCriterion(path, criterion.id, {
                      operator: event.target.value as WorkflowConditionOperator,
                    })
                  }
                >
                  {OPERATORS.filter(
                    ([value]) =>
                      !NUMBER_ONLY_OPERATORS.has(value) ||
                      isNumberVariable(criterion.variable) ||
                      // A loaded criterion keeps its operator listed so the select isn't blank.
                      value === criterion.operator,
                  ).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Value"
                size="small"
                value={criterion.value}
                disabled={viewOnly || criterion.operator === "is-empty"}
                onChange={(event) =>
                  updateCriterion(path, criterion.id, { value: event.target.value })
                }
              />
              {canRemoveCriterion ? (
                <Tooltip title="Remove criterion">
                  <span>
                    <IconButton
                      disabled={viewOnly}
                      aria-label="Remove criterion"
                      onClick={() => removeCriterion(path, criterion.id)}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
            </Box>
            );
          })}

          <Button
            disabled={viewOnly}
            className="workflow-canvas-dialog__mini-add"
            startIcon={<Add />}
            onClick={() =>
              updatePath(path.id, { criteria: [...path.criteria, createWorkflowCriterion()] })
            }
          >
            Add criterion
          </Button>

          <FormControl disabled={viewOnly} size="small" fullWidth>
            <InputLabel id={`${path.id}-route-label`}>Route to</InputLabel>
            <Select
              labelId={`${path.id}-route-label`}
              label="Route to"
              value={path.routeId}
              onChange={(event: SelectChangeEvent) =>
                updatePath(path.id, { routeId: event.target.value })
              }
            >
              <MenuItem value="" className="workflow-canvas-dialog__placeholder-option">
                Select a canvas
              </MenuItem>
              {routeOptions.map((canvas) => (
                <MenuItem key={canvas.id} value={canvas.id}>
                  {canvas.name}
                </MenuItem>
              ))}
              <MenuItem value={WORKFLOW_END_ROUTE}>{WORKFLOW_END_ROUTE_LABEL}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      ))}

      <Button
        disabled={viewOnly}
        className="workflow-canvas-dialog__mini-add"
        startIcon={<Add />}
        onClick={() => onChange([...paths, createWorkflowConditionPath()])}
      >
        Add path
      </Button>
    </Box>
  );
};

export default WorkflowConditionalPanel;
